import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import api, { testRunsAPI } from '../services/api'
import AdminPageWrapper from '../components/AdminPageWrapper'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorAlert from '../components/ErrorAlert'

const AdminTestRunsPage = () => {
  const [testRuns, setTestRuns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedTestRun, setSelectedTestRun] = useState(null)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [testRunDetails, setTestRunDetails] = useState(null)
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [reportHtml, setReportHtml] = useState('')
  const [reportLoading, setReportLoading] = useState(false)

  useEffect(() => {
    loadTestRuns()
  }, [])

  const loadTestRuns = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const testRunsData = await testRunsAPI.getTestRuns()
      setTestRuns(testRunsData || [])
      
    } catch (err) {
      console.error('Failed to load test runs:', err)
      setError('Failed to load test runs: ' + (err.response?.data?.detail || err.message))
    } finally {
      setLoading(false)
    }
  }

  const loadTestRunDetails = async (testRun) => {
    try {
      setSelectedTestRun(testRun)
      const details = await testRunsAPI.getTestRunDetails(testRun.id)
      setTestRunDetails(details)
      setDetailsDialogOpen(true)
    } catch (err) {
      console.error('Failed to load test run details:', err)
      setError('Failed to load test run details: ' + (err.response?.data?.detail || err.message))
    }
  }

  const loadTestRunReport = async (testRun) => {
    try {
      setReportLoading(true)
      setSelectedTestRun(testRun)
      
      // Use the axios instance which automatically handles authentication
      const response = await api.get(`/test-runs/${testRun.id}/report`, {
        responseType: 'text',
        headers: {
          'Accept': 'text/html'
        }
      })
      
      setReportHtml(response.data)
      setReportDialogOpen(true)
    } catch (err) {
      console.error('Failed to load test run report:', err)
      setError('Failed to load test run report: ' + (err.response?.data?.detail || err.message))
    } finally {
      setReportLoading(false)
    }
  }

  const getSuccessRateColor = (successRate) => {
    if (successRate >= 90) return 'success'
    if (successRate >= 75) return 'warning'
    return 'error'
  }

  const getSuccessRateIcon = (successRate) => {
    if (successRate >= 90) return <CheckCircleIcon />
    if (successRate >= 75) return <WarningIcon />
    return <ErrorIcon />
  }

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds.toFixed(1)}s`
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <AdminPageWrapper title="Test Runs & Loop Results">
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <Tooltip title="Refresh test runs">
          <IconButton onClick={loadTestRuns} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <ErrorAlert 
          message={error} 
          onClose={() => setError(null)}
          sx={{ mb: 3 }}
        />
      )}

      <Grid container spacing={3}>
        {testRuns.map((testRun) => (
          <Grid item xs={12} md={6} lg={4} key={testRun.id}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h2">
                    {testRun.scenario}
                  </Typography>
                  <Chip 
                    label={`${testRun.success_rate.toFixed(1)}%`}
                    color={getSuccessRateColor(testRun.success_rate)}
                    icon={getSuccessRateIcon(testRun.success_rate)}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {new Date(testRun.timestamp).toLocaleString()}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Performance:
                  </Typography>
                  <Typography variant="body2">
                    Avg: {formatDuration(testRun.average_duration)} 
                    ({formatDuration(testRun.min_duration)} - {formatDuration(testRun.max_duration)})
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Results ({testRun.iterations} iterations):
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      label={`${testRun.passed_count} passed`}
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                    {testRun.failed_count > 0 && (
                      <Chip 
                        label={`${testRun.failed_count} failed`}
                        color="error"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {testRun.error_count > 0 && (
                      <Chip 
                        label={`${testRun.error_count} errors`}
                        color="warning"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>

                <LinearProgress 
                  variant="determinate" 
                  value={testRun.success_rate} 
                  color={getSuccessRateColor(testRun.success_rate)}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    size="small" 
                    startIcon={<VisibilityIcon />}
                    onClick={() => loadTestRunDetails(testRun)}
                  >
                    Details
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<AssessmentIcon />}
                    onClick={() => loadTestRunReport(testRun)}
                    disabled={reportLoading}
                  >
                    Report
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {testRuns.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            🔍 No test runs found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Run some loop tests to see results here, or check the file system at test/test-runs/loops/
          </Typography>
          <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto' }}>
            <Typography variant="body2">
              <strong>To run loop tests:</strong><br/>
              • Use <code>python run_loop_test.py --scenario bid_exists --iterations 5</code><br/>
              • Or run <code>python run_invoice_test.py</code> and select option 5
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Test Run Details Dialog - placeholder for future implementation */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Test Run Details: {selectedTestRun?.scenario}
        </DialogTitle>
        <DialogContent>
          {testRunDetails ? (
            <Box>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Test Summary</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><strong>Scenario</strong></TableCell>
                          <TableCell>{testRunDetails.summary.scenario}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Timestamp</strong></TableCell>
                          <TableCell>{new Date(testRunDetails.summary.timestamp).toLocaleString()}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Total Iterations</strong></TableCell>
                          <TableCell>{testRunDetails.summary.iterations}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Success Rate</strong></TableCell>
                          <TableCell>{testRunDetails.summary.success_rate.toFixed(1)}%</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Duration</strong></TableCell>
                          <TableCell>
                            Avg: {formatDuration(testRunDetails.summary.average_duration)} 
                            (Min: {formatDuration(testRunDetails.summary.min_duration)}, 
                            Max: {formatDuration(testRunDetails.summary.max_duration)})
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Results</strong></TableCell>
                          <TableCell>
                            {testRunDetails.summary.passed_count} passed, {testRunDetails.summary.failed_count} failed, {testRunDetails.summary.error_count} errors
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              {testRunDetails.system_prompt && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">System Prompt</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                      <pre style={{ 
                        margin: 0, 
                        fontSize: '0.85rem', 
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {testRunDetails.system_prompt}
                      </pre>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {testRunDetails.kernel_functions && Object.keys(testRunDetails.kernel_functions).length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Kernel Functions ({Object.keys(testRunDetails.kernel_functions).length})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Function Name</strong></TableCell>
                            <TableCell><strong>Description</strong></TableCell>
                            <TableCell><strong>Module</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(testRunDetails.kernel_functions).map(([name, info]) => (
                            <TableRow key={name}>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{name}</TableCell>
                              <TableCell>{info.description || 'No description'}</TableCell>
                              <TableCell><Chip label={info.module} size="small" variant="outlined" /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {testRunDetails.iterations && testRunDetails.iterations.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Iteration Results ({testRunDetails.iterations.length})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Iteration</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Duration</strong></TableCell>
                            <TableCell><strong>Ticket ID</strong></TableCell>
                            <TableCell><strong>Final Status</strong></TableCell>
                            <TableCell><strong>Error</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {testRunDetails.iterations.map((iteration, index) => (
                            <TableRow key={index}>
                              <TableCell>{iteration.iteration}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={iteration.status} 
                                  size="small"
                                  color={iteration.status === 'passed' ? 'success' : iteration.status === 'failed' ? 'error' : 'warning'}
                                />
                              </TableCell>
                              <TableCell>{formatDuration(iteration.duration_seconds)}</TableCell>
                              <TableCell>{iteration.ticket_id || '-'}</TableCell>
                              <TableCell>{iteration.final_ticket_status || '-'}</TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {iteration.error_message || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <LoadingSpinner />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Test Run Report Dialog - Full Screen */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={() => setReportDialogOpen(false)}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: 'background.default',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Typography variant="h6">
            📊 Test Run Report: {selectedTestRun?.scenario}
          </Typography>
          <IconButton 
            onClick={() => setReportDialogOpen(false)}
            size="large"
          >
            <Typography variant="body2" sx={{ mr: 1 }}>✕</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {reportLoading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '50vh' 
            }}>
              <LoadingSpinner />
            </Box>
          ) : (
            <Box
              component="iframe"
              srcDoc={reportHtml}
              sx={{
                width: '100%',
                height: 'calc(100vh - 120px)', // Account for title bar
                border: 'none',
                bgcolor: 'white'
              }}
              title={`Test Run Report: ${selectedTestRun?.scenario}`}
            />
          )}
        </DialogContent>
      </Dialog>
    </AdminPageWrapper>
  )
}

export default AdminTestRunsPage